import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { sessionLabel } from '../../utils/leaveSession';
import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  OutlinedInput,
  Avatar,
  IconButton,
  Grid,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import TenantLeaveConfigPanel from '../Leave/TenantLeaveConfigPanel';
import LeaveTypePolicyManagement from '../Leave/LeaveTypePolicyManagement';
import LeaveLedgerExplorer from '../Leave/LeaveLedgerExplorer';
import ManagerDelegationPanel from '../Leave/ManagerDelegationPanel';
import EscalationsAndHolidaysPanel from '../Leave/EscalationsAndHolidaysPanel';
import Batch4ReconciliationCompOffYearEndPanel from '../Leave/Batch4ReconciliationCompOffYearEndPanel';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX, AVATAR_SX } from '../common/tableStyles';
import { useAdminLeaveRequests, useUpdateLeaveStatus, useAdminCancelLeave } from '../../hooks/useLeaves';
import { useQueryClient } from '@tanstack/react-query';
import { updateLeaveStatus } from '../../api';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/dateFormat';
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

function Leaves() {
  const statusColors = {
    PENDING: { bg: '#fffbeb', color: '#f59e0b', border: '#f59e0b33' },
    ACCEPTED: { bg: '#f0fdf4', color: '#16a34a', border: '#16a34a33' },
    DECLINED: { bg: '#fef2f2', color: '#ef4444', border: '#ef444433' },
    CANCELLED: { bg: '#f5f3ff', color: '#8b5cf6', border: '#8b5cf633' },
    WITHDRAWN: { bg: '#f5f3ff', color: '#8b5cf6', border: '#8b5cf633' },
  };

  // For a cancelled / withdrawn leave, surface WHO did it, read from the
  // status_reason the withdraw flow writes (e.g. "Cancelled by employee").
  // Today only employees can cancel/withdraw, so this resolves to "Employee";
  // an HR-cancel path writing "by HR" would show "HR" automatically.
  const cancelActor = (row) => {
    if (!['CANCELLED', 'WITHDRAWN'].includes(row.status)) return null;
    const r = (row.status_reason || '').toLowerCase();
    if (r.includes('by hr') || r.includes('by admin')) return 'HR';
    return 'Employee';
  };

  const [activeTab, setActiveTab] = useState(0);
  const { mutate: updateStatus } = useUpdateLeaveStatus();
  const { data: leaveRequests, error, isLoading } = useAdminLeaveRequests();

  // State for Filters, Sorting, and Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [endDate] = useState('');

  // Bulk selection + actions
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);   // action dialog is a bulk op
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkRunning = useRef(false);                 // sync re-entry guard (batching-proof)

  // Info Modal States
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);

  // Action Confirmation Modal States
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');

  // Admin "Cancel leave" (revoke an accepted leave on the employee's behalf).
  const { mutate: cancelLeave, isPending: isCancelling } = useAdminCancelLeave();
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // Arrived from the attendance "Pending" tile → focus that employee's requests.
  const location = useLocation();
  useEffect(() => {
    const empNo = location.state?.employeeNo;
    if (empNo) setSearchTerm(String(empNo));
  }, [location.state]);

  // Reset the selection whenever the visible set changes, so a stale tick from a
  // previous filter can't be bulk-acted by accident.
  useEffect(() => { setSelectedIds([]); }, [managerFilter, searchTerm, startDate, endDate]);

  // Extract unique reporting managers for the filter dropdown.
  const uniqueManagers = [...new Set(leaveRequests?.map(item => item.reporting_manager).filter(Boolean))];

  // Total pending across ALL requests (not just the current filter).
  const pendingCount = leaveRequests?.filter((r) => r.status === 'PENDING').length || 0;

  // Filtering Logic
  const filteredData = leaveRequests?.filter((row) => {
    const matchesSearch =
      (row.employee_name && row.employee_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (row.employee_no && String(row.employee_no).toLowerCase().includes(searchTerm.toLowerCase())) ||
      (row.mobile_number && row.mobile_number.includes(searchTerm)) ||
      (row.email && row.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesManager = managerFilter ? row.reporting_manager === managerFilter : true;

    const rowDate = new Date(row.created_at || row.start_date);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);

    const matchesDate = (!start || rowDate >= start) && (!end || rowDate <= end);

    return matchesSearch && matchesManager && matchesDate;
  });

  // Pending rows in the CURRENT filtered view = the selectable set for bulk actions.
  const selectablePending = (filteredData || []).filter((r) => r.status === 'PENDING');
  const allSelected = selectablePending.length > 0 && selectablePending.every((r) => selectedIds.includes(r.id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  // Sorting Logic: Latest date (Newest First)
  const sortedData = filteredData?.sort((a, b) => {
    const dateA = new Date(a.created_at || a.start_date);
    const dateB = new Date(b.created_at || b.start_date);
    return dateB - dateA;
  });

  const paginatedData = sortedData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenActionDialog = (request, status) => {
    setSelectedRequest(request);
    setNewStatus(status);
    setStatusReason('');
    setOpenActionDialog(true);
  };

  const handleCloseActionDialog = () => {
    setOpenActionDialog(false);
    setSelectedRequest(null);
    setNewStatus('');
    setStatusReason('');
    setBulkMode(false);
  };

  const handleViewDetails = (request) => {
    setSelectedDetails(request);
    setViewDetailsOpen(true);
  };

  // --- Bulk selection helpers ---
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : selectablePending.map((r) => r.id));
  };
  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Reuse the confirm dialog for the batch (captures one shared decline reason).
  const handleBulkOpen = (status) => {
    setNewStatus(status);
    setStatusReason('');
    setBulkMode(true);
    setOpenActionDialog(true);
  };

  // Apply the same status to every selected request, then refresh the list once.
  const runBulk = async (status, reason = '') => {
    if (bulkRunning.current) return;          // ignore a second submit while one is in flight
    // Act only on rows that are STILL pending in the current data — a selection
    // can go stale if the list refetched (e.g. someone else acted on a request).
    const pendingNow = new Set(selectablePending.map((r) => r.id));
    const ids = selectedIds.filter((id) => pendingNow.has(id));
    if (!ids.length) {
      toast.info('Those requests are no longer pending.', { autoClose: 4000 });
      setSelectedIds([]);
      return;
    }
    bulkRunning.current = true;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => updateLeaveStatus({ leave_request_id: id, status, status_reason: reason }))
      );
      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      const failIds = ids.filter((_, i) => results[i].status === 'rejected');
      queryClient.invalidateQueries({ queryKey: ['adminLeaveRequests'] });
      if (okCount) {
        toast.success(`${status === 'ACCEPTED' ? 'Approved' : 'Declined'} ${okCount} request${okCount > 1 ? 's' : ''}.`, { autoClose: 4000 });
      }
      if (failIds.length) {
        const names = failIds
          .map((id) => leaveRequests?.find((r) => r.id === id)?.employee_name)
          .filter(Boolean);
        const shown = names.slice(0, 3).join(', ');
        const more = names.length > 3 ? ` +${names.length - 3} more` : '';
        toast.error(`${failIds.length} failed${shown ? `: ${shown}${more}` : ''}.`, { autoClose: 6000 });
      }
      setSelectedIds([]);
    } finally {
      setBulkBusy(false);
      bulkRunning.current = false;
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!statusReason && newStatus === 'DECLINED') {
      alert('Please provide a reason for declining.');
      return;
    }
    if (bulkMode) {
      await runBulk(newStatus, statusReason);
    } else {
      updateStatus({
        leave_request_id: selectedRequest.id,
        status: newStatus,
        status_reason: statusReason
      });
    }
    handleCloseActionDialog();
  };

  const StatusChip = ({ status }) => {
    const styles = statusColors[status] || { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          backgroundColor: styles.bg,
          color: styles.color,
          border: 'none',
          fontWeight: 700,
          fontSize: '0.7rem',
          height: '24px',
          borderRadius: '8px'
        }}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <EventNoteIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Leave Requests
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Review and act on leave requests across the organisation
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`${pendingCount} Pending`}
            sx={{ bgcolor: '#fffbeb', color: '#f59e0b', fontWeight: 700, borderRadius: '10px' }}
          />
        </Stack>
      </Paper>

      {/* Module Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0', mb: { xs: 2, md: 3 } }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              minHeight: 48,
            },
          }}
        >
          <Tab label="Leave Requests" />
          <Tab label="Policy Governance" />
          <Tab label="Leave Types & Rules" />
          <Tab label="Ledger & Balances" />
          <Tab label="Manager Delegations" />
          <Tab label="Escalations & Holidays" />
          <Tab label="Reconciliation, Comp-Off & Year-End" />
        </Tabs>
      </Box>

      {activeTab === 1 && <TenantLeaveConfigPanel />}
      {activeTab === 2 && <LeaveTypePolicyManagement />}
      {activeTab === 3 && <LeaveLedgerExplorer />}
      {activeTab === 4 && <ManagerDelegationPanel />}
      {activeTab === 5 && <EscalationsAndHolidaysPanel />}
      {activeTab === 6 && <Batch4ReconciliationCompOffYearEndPanel />}

      {activeTab === 0 && (
        <>
          {/* Filters card */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 }, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search Name, ID..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            minWidth: { xs: '100%', sm: 280 },
            '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Manager</InputLabel>
          <Select
            value={managerFilter}
            onChange={(e) => setManagerFilter(e.target.value)}
            input={<OutlinedInput label="Manager" sx={{ borderRadius: '12px', bgcolor: '#f8fafc' }} />}
            MenuProps={SELECTED_MENU_PROPS}
          >
            <MenuItem value=""><em>All Managers</em></MenuItem>
            {uniqueManagers?.map((mgr) => (
              <MenuItem key={mgr} value={mgr}>{mgr}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          type="date"
          size="small"
          label="Date Range"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}
        />
      </Paper>

      {/* Content Section */}
      {isLoading ? (
        <Paper elevation={0} sx={CARD_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
            <CircularProgress size={40} thickness={4} />
          </Box>
        </Paper>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: '14px' }}>Failed to load leave requests. Please try again.</Alert>
      ) : (
        <>
          {selectedIds.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75, mb: { xs: 2, md: 3 }, borderRadius: '16px', bgcolor: `${ACCENT}0d`, border: `1px solid ${ACCENT}33` }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: ACCENT }}>
                {selectedIds.length} selected
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" variant="contained" color="success" disableElevation disabled={bulkBusy}
                onClick={() => handleBulkOpen('ACCEPTED')}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}>
                Approve Selected
              </Button>
              <Button size="small" variant="contained" color="error" disableElevation disabled={bulkBusy}
                onClick={() => handleBulkOpen('DECLINED')}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}>
                Decline Selected
              </Button>
              <Button size="small" variant="text" disabled={bulkBusy}
                onClick={() => setSelectedIds([])}
                sx={{ textTransform: 'none', fontWeight: 700, color: '#6b7280' }}>
                Clear
              </Button>
            </Box>
          )}
          <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4', boxShadow: '0 6px 24px rgba(17,24,39,0.05)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ ...HEAD_CELL_SX }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleSelectAll}
                      disabled={selectablePending.length === 0}
                    />
                  </TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX }}>REQUESTED ON</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX }}>EMPLOYEE</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX }}>LEAVE TYPE</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX }}>DURATION</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX }}>STATUS</TableCell>
                  <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData?.length > 0 ? paginatedData.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => handleViewDetails(row)}
                    sx={{ ...TABLE_ROW_SX, cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox" sx={{ ...BODY_CELL_SX }} onClick={(e) => e.stopPropagation()}>
                      {row.status === 'PENDING' && (
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(row.id)}
                          onChange={() => toggleSelectOne(row.id)}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, py: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatDate(row.created_at)}
                        </Typography>
                        {row.status === 'PENDING' && (
                          <Chip label="NEW" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#eef4ff', color: ACCENT }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={AVATAR_SX}>
                          {row.employee_name?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>{row.employee_name}</Typography>
                          <Typography variant="caption" sx={{ color: '#6B7280' }}>ID: {row.employee_no}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>{row.leave_type}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                        {formatDate(row.start_date)} <span style={{ color: '#9CA3AF', margin: '0 4px' }}>→</span> {formatDate(row.end_date)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, py: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.4 }}>
                        <StatusChip status={row.status} />
                        {cancelActor(row) && (
                          <Typography variant="caption" sx={{ color: '#8b5cf6', fontWeight: 600, lineHeight: 1 }}>
                            by {cancelActor(row)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, py: 2, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton size="small" onClick={() => handleViewDetails(row)} sx={{ color: ACCENT, bgcolor: `${ACCENT}14` }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {row.status === 'PENDING' && (
                          <>
                            <Button
                              variant="outlined" color="success" size="small"
                              onClick={() => handleOpenActionDialog(row, 'ACCEPTED')}
                              sx={{ px: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined" color="error" size="small"
                              onClick={() => handleOpenActionDialog(row, 'DECLINED')}
                              sx={{ px: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                            >
                              Decline
                            </Button>
                          </>
                        )}
                        {row.status === 'ACCEPTED' && (
                          <Button
                            variant="outlined" size="small"
                            onClick={() => { setCancelReason(''); setCancelTarget(row); }}
                            sx={{ px: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 700, color: '#8b5cf6', borderColor: '#8b5cf655', '&:hover': { borderColor: '#8b5cf6', bgcolor: '#f5f3ff' } }}
                          >
                            Cancel
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8, border: 'none' }}>
                      <Stack alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <EventNoteIcon />
                        </Box>
                        <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No leave requests found.</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredData?.length || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                // Shift controls left so they don't sit under the floating chat button
                '& .MuiTablePagination-toolbar': { pr: { xs: 2, sm: 14 } },
              }}
            />
          </TableContainer>
        </>
      )}

        </>
      )}

      {/* Leave Details Modal (The Indexed Details View) */}
      <Dialog
        open={viewDetailsOpen}
        onClose={() => setViewDetailsOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}
      >
        <DialogTitle sx={{ pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlinedIcon color="primary" />
            <Typography variant="h6" fontWeight={800}>Leave Details</Typography>
          </Stack>
          {selectedDetails && <StatusChip status={selectedDetails.status} />}
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {selectedDetails && (
            <Grid container spacing={4}>
              {/* Employee Info */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2.5, borderRadius: '16px', bgcolor: '#f8fafc', border: '1px solid #eef0f4' }}>
                  <Typography variant="subtitle2" sx={{ color: '#6B7280', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonOutlineIcon fontSize="small" /> EMPLOYEE INFORMATION
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Name:</Typography>
                      <Typography variant="body2" fontWeight={600}>{selectedDetails.employee_name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Employee ID:</Typography>
                      <Typography variant="body2" fontWeight={600}>{selectedDetails.employee_no}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Designation:</Typography>
                      <Typography variant="body2" fontWeight={600}>{selectedDetails.designation || 'N/A'}</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>

              {/* Leave Info */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2.5, borderRadius: '16px', bgcolor: '#f8fafc', border: '1px solid #eef0f4' }}>
                  <Typography variant="subtitle2" sx={{ color: '#6B7280', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarMonthIcon fontSize="small" /> LEAVE PERIOD
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Leave Type:</Typography>
                      <Typography variant="body2" fontWeight={600}>{selectedDetails.leave_type}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Starts:</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatDate(selectedDetails.start_date)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Ends:</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatDate(selectedDetails.end_date)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Duration:</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedDetails.leave_days} day{Number(selectedDetails.leave_days) === 1 ? '' : 's'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Session:</Typography>
                      <Typography variant="body2" fontWeight={600}>{sessionLabel(selectedDetails.session_from, selectedDetails.session_to)}</Typography>
                    </Box>
                    {selectedDetails.document && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">Supporting Document:</Typography>
                        <Button
                          size="small"
                          startIcon={<AttachFileIcon fontSize="small" />}
                          href={selectedDetails.document}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: 'none', fontWeight: 600, color: ACCENT }}
                        >
                          View Document
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Grid>

              {/* Reason Section */}
              <Grid item xs={12}>
                <Box sx={{ p: 2.5, borderRadius: '16px', border: '1px dashed #e5e7eb' }}>
                  <Typography variant="subtitle2" sx={{ color: '#6B7280', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentOutlinedIcon fontSize="small" /> REASON FOR LEAVE
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.6 }}>
                    {selectedDetails.reason || "No reason provided."}
                  </Typography>
                </Box>
              </Grid>

              {/* Status History */}
              {(selectedDetails.approved_date || selectedDetails.declined_date) && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2.5, borderRadius: '16px', bgcolor: '#f0fdf4', border: '1px solid #16a34a33' }}>
                    <Typography variant="subtitle2" sx={{ color: '#166534', mb: 1 }}>ACTION LOG</Typography>
                    <Typography variant="body2">
                      Request was <strong>{selectedDetails.status.toLowerCase()}</strong> on {formatDate(selectedDetails.approved_date || selectedDetails.declined_date)}.
                    </Typography>
                    {selectedDetails.status_reason && (
                      <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: '8px' }}>
                        <strong>Note:</strong> {selectedDetails.status_reason}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={() => setViewDetailsOpen(false)} variant="outlined" sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, minWidth: '120px' }}>
            Close
          </Button>
          {selectedDetails?.status === 'PENDING' && (
            <>
              <Button onClick={() => { setViewDetailsOpen(false); handleOpenActionDialog(selectedDetails, 'DECLINED'); }} color="error" variant="contained" disableElevation sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, minWidth: '120px' }}>
                Decline
              </Button>
              <Button onClick={() => { setViewDetailsOpen(false); handleOpenActionDialog(selectedDetails, 'ACCEPTED'); }} color="success" variant="contained" disableElevation sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, minWidth: '120px' }}>
                Approve Now
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Status Action Confirmation Modal */}
      <Dialog open={openActionDialog} onClose={handleCloseActionDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {newStatus === 'ACCEPTED' ? 'Approve Leave' : 'Decline Leave'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #eef0f4' }}>
              You are about to <strong>{newStatus === 'ACCEPTED' ? 'approve' : 'decline'}</strong>{' '}
              {bulkMode
                ? <strong>{selectedIds.length} selected leave request{selectedIds.length > 1 ? 's' : ''}</strong>
                : <>the leave request for <strong>{selectedRequest?.employee_name}</strong></>}.
            </Typography>
            <TextField
              autoFocus
              label={newStatus === 'ACCEPTED' ? "Remarks (Optional)" : "Reason for Declining (Required)"}
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseActionDialog} variant="text" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}>Cancel</Button>
          <Button
            onClick={handleConfirmStatusChange}
            color={newStatus === 'ACCEPTED' ? "success" : "error"}
            variant="contained"
            disableElevation
            disabled={bulkBusy}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 4, py: 1 }}
          >
            Confirm {newStatus === 'ACCEPTED' ? 'Approval' : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* HR Cancel Leave Confirmation */}
      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => { if (!isCancelling) setCancelTarget(null); }}
        fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '24px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Cancel Leave</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', p: 2, bgcolor: '#f5f3ff', borderRadius: '12px', border: '1px solid #8b5cf633' }}>
              You are about to <strong>cancel</strong> the {cancelTarget?.leave_type} for{' '}
              <strong>{cancelTarget?.employee_name}</strong>
              {cancelTarget ? ` (${formatDate(cancelTarget.start_date)} → ${formatDate(cancelTarget.end_date)})` : ''}.
              The {cancelTarget?.leave_days || ''} leave day(s) will be restored to their balance and the employee
              will be notified. This is recorded as cancelled by HR.
            </Typography>
            <TextField
              autoFocus
              label="Reason (Optional)"
              fullWidth variant="outlined" multiline rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCancelTarget(null)} disabled={isCancelling} variant="text" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}>Keep</Button>
          <Button
            onClick={() => cancelLeave(
              { leave_request_id: cancelTarget.id, reason: cancelReason },
              { onSettled: () => { setCancelTarget(null); setCancelReason(''); } }
            )}
            disabled={isCancelling}
            variant="contained" disableElevation
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 4, py: 1, bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
          >
            {isCancelling ? 'Cancelling…' : 'Cancel Leave'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Leaves;
